import { CirclePlus, Gamepad2, Users } from "lucide-react";
import Image from "next/image";
import { GlassButton } from "../GlassButton";
import { NumberCounter } from "./NumberCounter";

interface TierItem {
  label: string;
  prizeImage?: string;
  description: string;
}

export const MotivationState = ({
  initialNumber,
  targetNumber,
  gameName,
  motivationImage,
  onGetStartedClick,
  prizeImage,
  gameId,
}: {
  initialNumber: number;
  targetNumber: number;
  gameName: string;
  motivationImage?: string;
  prizeImage?: string;
  onGetStartedClick: () => void;
  gameId?: string;
}) => {
  const tiers: TierItem[] = [
    {
      label: "TIER 1",
      prizeImage: prizeImage,
      description: "Receive your first reward crate when you follow on play",
    },
    {
      label: "TIER 2",
      prizeImage: prizeImage,
      description: "Get involved to unlock the next tier of rewards",
    },
    {
      label: "TIER 3",
      prizeImage: prizeImage,
      description: "Gain sneak peaks and early access to the game",
    },
  ];
  return (
    <>
      {/* CVP - Central Value Prop */}
      <p className="font-suisse text-xs leading-none text-foreground pb-2">
        JOIN
      </p>

      {/* Animated Number Display */}
      <NumberCounter
        initialNumber={initialNumber}
        targetNumber={targetNumber}
      />

      <p className="font-suisse text-xs leading-[1.81] text-foreground mt-2">
        FANS
      </p>

      {/* Game Title & CTA */}
      <p className="font-suisse text-lg font-bold leading-7 mt-4">{gameName}</p>
      <p className="font-suisse text-2xl text-center font-normal leading-8 max-w-[20rem]">
        Get Exclusive Community Rewards
      </p>

      {/* Motivation Image */}
      {motivationImage ? (
        <Image
          src={motivationImage}
          alt="Rewards preview"
          height={200}
          width={300}
          className="object-contain"
        />
      ) : null}

      {/* Plus Icon & Key CTAs */}
      <CirclePlus className="-mb-1 size-6 shrink-0 text-foreground" />

      <div className="flex w-full items-start justify-center gap-1 pb-2 pt-0.5">
        {/* Left CTA */}
        <span className="flex min-w-0 grow basis-0 flex-col items-center gap-2.5 px-2">
          <Users className="size-6 shrink-0 text-foreground" />
          <p className="font-suisse text-xs font-medium leading-tight text-foreground text-center">
            See your feedback
            <br />
            shape the game
          </p>
        </span>

        {/* Divider */}
        <span className="h-16 w-0.5 shrink-0 rounded-full bg-foreground" />

        {/* Right CTA */}
        <span className="flex min-w-0 grow basis-0 flex-col items-center gap-2.5 px-2">
          <Gamepad2 className="size-6 shrink-0 text-foreground" />
          <p className="font-suisse text-xs font-medium leading-tight text-foreground text-center">
            Get early access to
            <br />
            Alpha and Beta Releases
          </p>
        </span>
      </div>
      {/* Get Started Button */}
      <GlassButton
        onClick={onGetStartedClick}
        className="flex-col !h-auto w-full px-20 py-3 mt-2"
        gameId={gameId}
        gameName={gameName}
        control="GetStarted"
        variant="cta"
      >
        <p className="text-base font-bold leading-none text-foreground min-w-[8rem]">
          Get Started
        </p>
        <p className="text-xs text-foreground min-w-[8rem]">
          Exclusive early rewards
        </p>
      </GlassButton>

      {/* Tiers Section */}
      <div className="flex w-full flex-col items-center mt-2">
        {tiers.map((tier) => (
          <div key={tier.label} className="flex w-full flex-col items-center">
            {/* Divider */}
            <div className="h-5 w-0.5 shrink-0 rounded-full bg-foreground" />

            {/* Tier Content */}
            <div className="flex w-full items-center gap-5">
              {/* Tier Label - Left Side */}
              <span className="flex flex-1 justify-end">
                <p className="font-roboto text-sm font-bold uppercase leading-5 text-foreground text-right [text-shadow:rgba(0,0,0,0.32)_0px_1px_4px,rgba(0,0,0,0.16)_0px_4px_8px]">
                  {tier.label}
                </p>
              </span>

              {/* Prize Image - Center */}
              <div className="relative size-20 shrink-0">
                {tier.prizeImage ? (
                  <div className="relative size-20 shrink-0">
                    <Image
                      src={tier.prizeImage}
                      alt={tier.label}
                      fill
                      className="object-cover"
                    />
                  </div>
                ) : null}
              </div>

              {/* Description - Right Side */}
              <span className="flex flex-1">
                <p className="font-suisse text-xs font-medium leading-[1.2] text-foreground max-w-[6rem]">
                  {tier.description}
                </p>
              </span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
